import FormBuilderModel from "../../../../common/models/tracked/formBuilderModel";
import WorkflowStep from "./workflowStep";
import Vue from "vue";
import Guid from "~/helpers/guid";
import ConditionalRuleSetFactory from "../../../../common/models/conditionalRules/conditionalRuleSetFactory";
import IConditionalRuleContainer from "../../../../common/models/conditionalRules/conditionalRuleContainer";
import IWorkflowStepContainer from "./iworkflowStepContainer";
import {RelationType} from "../../../../common/models/relationType";
import IRepo from "../../../../common/interfaces/repo";
import Repo from "../../../../common/state/modules/repo";
import WorkflowStepActionJumpTo from "./workflowStepActionJumpTo";
import WorkflowStepActionJumpToFactory from "./workflowStepActionJumpToFactory";
import Validatable from "../../../../common/mixins/validatable";
import WorkflowStepActionValidation from "../../../../common/models/navigableValidations/workflowStepActionValidation";
import FormElementConditionalRuleSet from "../conditionalRules/formElementConditionalRuleSet";
import FormElementConditionalFields from "../conditionalRules/formElementConditionalFields";
import {ConditionalFieldDefinition} from "../../../../common/models/conditionalRules/conditionalFieldDefinition";
import FormElement from "../formElements/formElement";
import {Hash} from "../../../../common/interfaces/hashInterfaces";
import {IWorkflowStepAction} from '~/interfaces/workflowStepInterface'
import {ModelType} from "~/enums/modelType";
import {IImposter, IImposterOptions} from "~/interfaces/imposterInterfaces";
import Imposter from "~/models/imposter";
import {GenericType} from "~/interfaces/genericTypeInterfaces";
import {IVOptionSetLocal} from "~/interfaces/vSelectInterfaces";
import {
  ConditionalRuleBuiltInValueType,
  labelForConditionalRuleBuiltInType,
  valueForConditionalRuleBuiltInType
} from "~/models/conditionalRules/conditionalRuleBuiltInValueType";

export default class WorkflowStepAction extends Validatable(FormBuilderModel) implements IConditionalRuleContainer, IWorkflowStepContainer, IWorkflowStepAction {
  client_id: string = Guid.create();
  title: string;
  jump_to: WorkflowStepActionJumpTo;
  sort_order: number;
  style: string = '';
  is_revision_mode_enabled: boolean;
  createEmbeddedObject = {};
  jump_to_conditional_rule_sets: Hash<FormElementConditionalRuleSet>;
  readonly JumpToFieldName = 'jump_to';

  static repo: IRepo<WorkflowStepAction> = new Repo<WorkflowStepAction>('workflowStepActions', WorkflowStepAction);

  constructor(workflowStep: WorkflowStep, json: any = {}) {
    super();
    this.defineProperty('client_id', json['client_id'] || Guid.create());
    this.defineProperty('title', '');
    const initialSortOrder = json['sort_order'] === 0 || json['sort_order'] ? json['sort_order'] : Object.keys(workflowStep.actions).length;
    this.defineProperty('sort_order', initialSortOrder);
    this.defineProperty('style', 'green');
    this.defineProperty('is_revision_mode_enabled', false)

    for (const definition of FormElementConditionalFields.WORKFLOW_STEP_ACTION_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent: this, ruleJson) => {
        return ConditionalRuleSetFactory.createForContainer(parent, definition, ruleJson, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }

    this.loadFromJson(json, workflowStep.rootModel);
    if (!this.jump_to) {
      WorkflowStepActionJumpToFactory.create(this);
    }

    this.rootModel = workflowStep.rootModel;
    this.parent = workflowStep;
    WorkflowStepAction.repo.add(this);
    Vue.set(workflowStep.actions, this.client_id, this);
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {};
    models[this.JumpToFieldName] = { type: WorkflowStepActionJumpTo, relationType: RelationType.singleObject };
    for (const {fieldName} of FormElementConditionalFields.WORKFLOW_STEP_ACTION_FIELDS) {
      models[`${fieldName}_conditional_rule_sets`] = { type: FormElementConditionalRuleSet, relationType: RelationType.dictionary };
    }
    return models;
  }

  get EMBEDDED_HASHES(): string[] {
    return [this.JumpToFieldName];
  }

  //******************** VALIDATABLE ********************//
  _validation: WorkflowStepActionValidation<WorkflowStepAction> = new WorkflowStepActionValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      default:
        return propName;
    }
  }
  //******************** VALIDATABLE END ********************//

  get titleOrDefault(): string {
    return this.title;
  }

  get rootName(): string {
    return 'actions';
  }

  get isCollectionRoot(): boolean {
    return true;
  }

  get jump_to_client_id() {
    return this.jump_to.to_client_id;
  }

  set jump_to_client_id(val) {
    this.jump_to.to_client_id = val;
  }

  *jumpToClientIds(): IterableIterator<string> {
    yield this.jump_to.to_client_id;
    const conditionalRuleSets = this.getConditionalRuleSets(this.JumpToFieldName);
    for (const key in conditionalRuleSets) {
      const ruleSet = conditionalRuleSets[key];
      yield ruleSet.conditional_value.to_client_id;
    }
  }

  get hasConditionalBranches() {
    return Object.keys(this.getJumpToConditionals()).length > 0;
  }

  getConditionalRuleSets(fieldName: string): { [key: string]: FormElementConditionalRuleSet } {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): FormElementConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return Object.keys(this.getConditionalRuleSets(fieldName)).map(key => ruleSets[key])
      .sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  getJumpToConditionals() {
    return this.getConditionalRuleSets(this.JumpToFieldName);
  }

  getSortedJumpToConditionals(): FormElementConditionalRuleSet[] {
    return this.getSortedConditionalRuleSets(this.JumpToFieldName);
  }

  get nextSteps(): any[] {
    return this.jumpTos.map(k => this.rootModel.workflow_steps[k.to_client_id] ? this.rootModel.workflow_steps[k.to_client_id] : k);
  }

  get jumpTos(): WorkflowStepActionJumpTo[] {
    const values = [];
    const ruleSets = this.getSortedJumpToConditionals();

    Object.keys(ruleSets).forEach(k => values.push(ruleSets[k].conditional_value));
    values.push(this.jump_to);
    return values
  }

  createNewRuleSet(fieldName: string, definition: ConditionalFieldDefinition): FormElementConditionalRuleSet {
    return new FormElementConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): FormElement {
    return FormElement.repo.find(id);
  }

  getReferencableControls(): FormElement[] {
    return this.rootModel.referenceableFormElementsFor(null);
  }

  get conditionalRulesBuiltInValueOptions(): IVOptionSetLocal {
    const options = [{
      referenceNameOrDefault: labelForConditionalRuleBuiltInType(ConditionalRuleBuiltInValueType.FormSubmissionOriginalSubmittedAt),
      client_id: valueForConditionalRuleBuiltInType(ConditionalRuleBuiltInValueType.FormSubmissionOriginalSubmittedAt),
    }];

    this.rootModel.workflowStepsArray.forEach(workflowStep => {
      options.push({
        referenceNameOrDefault: labelForConditionalRuleBuiltInType(ConditionalRuleBuiltInValueType.FormSubmissionStepSubmittedOnDate, workflowStep.titleOrDefault),
        client_id: valueForConditionalRuleBuiltInType(ConditionalRuleBuiltInValueType.FormSubmissionStepSubmittedOnDate, workflowStep.client_id),
      });

      if (workflowStep.isPaymentStep) {
        options.push({
          referenceNameOrDefault: labelForConditionalRuleBuiltInType(ConditionalRuleBuiltInValueType.FormSubmissionStepPaymentMethod, workflowStep.titleOrDefault),
          client_id: valueForConditionalRuleBuiltInType(ConditionalRuleBuiltInValueType.FormSubmissionStepPaymentMethod, workflowStep.client_id),
        })
      }
    });

    return {
      valueKey: 'client_id',
      labelKey: 'referenceNameOrDefault',
      heading: 'Workflow Steps',
      options,
    }
  }

  get conditionalRulesPaymentStepBuiltInValueOptions(): IVOptionSetLocal {
    const options = []
    this.rootModel.workflowStepsArray.forEach(workflowStep => {
      if (workflowStep.isPaymentStep) {
        options.push({
          referenceNameOrDefault: labelForConditionalRuleBuiltInType(ConditionalRuleBuiltInValueType.FormSubmissionStepPaymentMethod, workflowStep.titleOrDefault),
          client_id: valueForConditionalRuleBuiltInType(ConditionalRuleBuiltInValueType.FormSubmissionStepPaymentMethod, workflowStep.client_id),
        })
      }
    });

    return {
      valueKey: 'client_id',
      labelKey: 'referenceNameOrDefault',
      heading: 'Workflow Steps',
      options,
    }
  }

  get genericType(): GenericType {
    return ModelType.Object
  }

  imposterFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposter {
    return new Imposter({ ...this, isCollection: false }, options)
  }
}
